import { Component, OnInit, OnDestroy, HostListener, ViewContainerRef, AfterViewInit, ElementRef } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { of, EMPTY, interval, Subject } from 'rxjs';
import { finalize, first, switchMap, take, takeUntil } from 'rxjs/operators';
import has from 'lodash-es/has';
import isNil from 'lodash-es/isNil';
import get from 'lodash-es/get';

import { ANALYTICS_TIMEOUT } from '@finder/shared/constants/app.constants';
import { BLOCK_TYPES, COMPONENT_TYPES } from './detail.constants';
import { DETAIL_ANIMATIONS } from './detail.animations';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { FinderPersonalizationService } from '@finder/shared/services/finder-analytics/finder-personalization.service';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { MapAnimationEvent, MapInterface, MapButtonOptions } from '@finder/shared/components/map/map/map.interface';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { RouteStateService } from '@finder/shared/services/route-state-service/route-state-service';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { WebLinkValidationService } from '@finder/shared/services/web-link-validation/web-link-validation.service';
import { InterstitialService } from '@finder/shared/services/interstitial-service/interstitial-service.service';
import { InterstitialModal } from '@finder/shared/services/interstitial-service/interstitial-modal.interface';
import { ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { WebSocketsService } from '@finder/shared/services/websockets/websockets.service';

@Component({
    selector: 'app-detail',
    templateUrl: './detail.component.html',
    styleUrls: ['./detail.component.scss'],
    animations: [...DETAIL_ANIMATIONS]
})
export class DetailComponent implements OnInit, OnDestroy, AfterViewInit {
    svcResponse: any;
    mapExpanded: boolean;
    mapEvent: MapAnimationEvent;
    contentNavigation: any;
    showKnowBeforeYouGo = false;
    showDisclaimer = false;
    showExtraEnchantment = false;
    showPackageCards = false;
    showPackageCardsSeparator = false;
    showRelatedActivities = false;
    showPromos = false;
    showGAV = false;
    showMoreLikeThis = false;
    showStoryCards = false;
    showParticipatingRestaurants = false;
    showSeatingChart = false;
    showMap = false;
    isNative = false;
    showErrorPage = false;
    showAvailabilityCalendar = false;
    showPricingGrid = false;
    showTextBlocks = false;
    showVisualItinerary = false;
    showStepBlock = false;
    showTutorial = false;
    showSharedTable = false;
    useComponentOrder = false;
    isAndroidNative = false;
    resizeMap;
    related; // Used in HTML
    more; // Used in HTML
    mediaEngineData: any;

    BLOCK_TYPES = BLOCK_TYPES;
    COMPONENT_TYPES = COMPONENT_TYPES;

    private urlParams: ParamMap;
    private p13nLoaded = new Subject();
    private destroy$ = new Subject<void>();
    private recenterControl: MapButtonOptions;
    private map: MapInterface;

    protected interstitialModal: InterstitialModal;

    get mapState() {
        return this.mapExpanded || !this.showStoryCards ? 'expanded' : 'shrunk';
    }

    get showNdre() {
        return this.showKnowBeforeYouGo || this.showExtraEnchantment || this.showPackageCards;
    }

    constructor(
        private route: ActivatedRoute,
        private loadingService: LoadingService,
        private logger: LoggerService,
        private modalService: ModalService,
        private finderAnalyticsService: FinderAnalyticsService,
        private webLinkValidationService: WebLinkValidationService,
        private finderPersonalizationService: FinderPersonalizationService,
        private routeState: RouteStateService,
        private debugPanelService: DebugPanelService,
        private viewContainerRef: ViewContainerRef,
        private urlParamsService: UrlParamsService,
        private interstitialService: InterstitialService,
        private elementRef: ElementRef,
        private webSocketService: WebSocketsService,
        private viewportDetection: ViewportDetection,
    ) { }

    // Listen for GAV event to show GAV content
    @HostListener('window:gavChanged', ['$event'])
    onGAVChanged(event) {
        this.showGAV = true;
    }

    // Listen for p13n response
    @HostListener('document:personalization.responseReady', ['$event'])
    onP13nReady(event) {
        const locations = get(event, 'detail.locations');
        if (!locations) {
            this.logger.warn('FINDER PERSONALIZATION SVC -- No locations found!');

            return;
        }

        // Set service properties
        this.svcResponse.locations = locations;
        this.svcResponse.promos = this.finderPersonalizationService.mapP13nResponse(this.svcResponse);

        // Update promo tiles once data bound
        this.checkPromos();

        // Update subscribers
        this.p13nLoaded.next(true);
    }

    ngOnInit() {
        this.showErrorPage = false;
        this.modalService.extendDefaultConfigs({hideDismissButton: this.isAndroidNative});

        const snapshotData = get(this.route.snapshot.data, 'svc');

        if (!snapshotData) {
            return;
        }

        if (snapshotData.dfmData) {
            this.svcResponse = {};
            this.svcResponse.dfmData = snapshotData.dfmData;

            return;
        }

        this.finderAnalyticsService.initAnalyticsLibrary(snapshotData.analytics);
        this.processDetailData(snapshotData);
        this.processMediaBundleData(snapshotData);
        this.setupInterstitialData();
        this.startWebSocketConnection(snapshotData);
        this.setupTheming(snapshotData);

        this.routeState.pathParam()
            .pipe(
                switchMap((params: any) => {
                    /**
                     * Setup the param service so analytics will work.
                     * This method expects a map object. This will convert the object to a map
                     */
                    this.urlParamsService.setParams(new Map(Object.entries(params)));

                    return of(EMPTY);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(
                () => {}
            );

        this.viewportDetection.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.setupTheming(snapshotData));
    }

    ngAfterViewInit() {
        this.debugPanelService.createDebugPanel(this.viewContainerRef, this.svcResponse);
    }

    processDetailData(data) {
        // validate detail page web link
        this.webLinkValidationService.validateWebLink(data.webLink);

        // Make sure we have the guest model details first before triggering any p13n
        this.finderAnalyticsService.getGuestModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe(guestModel => {
                // Check if we have CMS defined promo tiles, because if we do we don't
                // want to trigger the automated p13n logic cause it will overwrite
                // the CMS content.
                const hasCmsPromos = data.promos && data.promos.length > 0;

                // Get the p13n entityType denylist
                const p13nEntityDenylist = get(data, 'p13nEntityDenylist', []);

                // If there are no CMS promotions and the current `entityType` is not denylisted
                // call the p13n service
                if (!hasCmsPromos && p13nEntityDenylist.indexOf(data.type) === -1) {
                    // Load p13n content/analytics
                    this.finderPersonalizationService.loadP13n();
                    this.logger.log('P13N: ENABLED');
                } else {
                    this.logger.log('P13N: DISABLED');
                }

                // Update the analytics model
                this.finderAnalyticsService.updateAnalyticsModel(
                    this.urlParams,
                    guestModel,
                    data.id,
                    data.aagData.title);

                // We'll setup a timer in case the p13n completed event never fires
                const p13nTimer = setTimeout(() => {
                    this.finderAnalyticsService.updateAnalyticsLoader();
                }, ANALYTICS_TIMEOUT);

                // If the event DOES fire, we can cancel the timer and call the model update
                this.p13nLoaded
                    .pipe(
                        first(),
                        finalize(() => clearTimeout(p13nTimer)),
                    )
                    .subscribe(() => {
                        this.finderAnalyticsService.updateAnalyticsLoader();
                    });
            });
        // Set service response
        this.svcResponse = data;

        // Perform content checks/loading
        this.checkStoryCards();
        this.checkRelatedActivities();
        this.checkPromos();
        this.checkMoreLikeThis();
        this.checkPackageCards();
        this.checkExtraEnchantment();
        this.checkKnowBeforeYouGo();
        this.checkDisclaimer();
        this.checkParticipatingRestaurants();
        this.checkSeatingChart();
        this.checkMap();
        this.checkContentNavigation();
        this.checkAvailabilityCalendar();
        this.checkPricingGrid();
        this.checkTextBlocks();
        this.checkVisualItinerary();
        this.checkComponentOrder();
        this.checkStepBlock();
        this.checkTutorialBlock();
        this.checkSharedTable();

        // Finally, set loaded state to hide spinner
        this.loadingService.setLoadingState(true);
    }

    processMediaBundleData(bundle: any = {}) {
        // If there's no response from the media engine we get a null object
        if (!bundle || bundle instanceof Error || bundle.error) {
            // Just log, there's no reason to hide the UI
            this.logger.warn('Media SVC -- Error while getting the response', get(bundle, 'error.errors[0].typeId', ''));

            return;
        }
        // Set media engine data
        this.setMediaEngineData(bundle);
    }

    mapIsReady(mapObj: MapInterface) {
        this.map = mapObj;
    }

    startResizingMap() {
        // If we don't have an event or we're not changing one of two properties stop
        // Stop if there's no map component
        if (!this.mapEvent || !this.map) {
            return;
        }

        // The rxjs commands here simulate redrawing the map over a 500ms
        // time period
        this.resizeMap = interval(50)
            .pipe(
                takeUntil(this.destroy$),
                take(10),
                finalize(() => {
                    // Zoom in on parade route
                    this.map.focusOnParadeRoute();

                    // The map can be expanded if there are not storycards
                    // in that case we dont need to add the map controls
                    if (!this.showStoryCards) {
                        return;
                    }

                    // If the map is expanded
                    if (this.mapExpanded) {
                        // Add map controls
                        this.map.setZoomControls({
                            position: 'topright'
                        });


                        // Add recenter control
                        this.map.addRecenterControl(this.recenterControl);
                        // Enable dragging
                        this.map.enableDragging();
                    } else {
                        this.map.removeZoomControl();
                        this.map.removeRecenterControl(this.recenterControl);
                        this.map.disableDragging();
                        this.map.closePopups();
                    }
                })
            )
            .subscribe(() => {
                // Redraw to fit container
                this.map.redrawMap();
            });
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();

        this.webSocketService.cleanUp();
    }

    toggleMapState(event) {
        this.mapEvent = event;
        this.mapExpanded = this.mapEvent.state;
    }

    onClickNativeHeader() {
    }

    checkContentNavigation() {
        this.contentNavigation = get(this.svcResponse, 'contentNavigation');
    }

    checkAvailabilityCalendar() {
        if (has(this.svcResponse, 'availabilityCalendar')) {
            this.showAvailabilityCalendar = true;
        }
    }

    checkPricingGrid() {
        if (has(this.svcResponse, 'pricingGrid')) {
            this.showPricingGrid = true;
        }
    }

    checkTextBlocks() {
        if (has(this.svcResponse, 'textBlocks')) {
            this.showTextBlocks = true;
        }
    }

    checkComponentOrder() {
        if (has(this.svcResponse, 'componentOrder')) {
            this.useComponentOrder = true;
        }
    }

    checkVisualItinerary() {
        if (has(this.svcResponse, 'visualItinerary')) {
            this.showVisualItinerary = true;
        }
    }

    checkStepBlock() {
        if (has(this.svcResponse, 'stepBlock')) {
            this.showStepBlock = true;
        }
    }

    checkTutorialBlock() {
        if (has(this.svcResponse, 'tutorial')) {
            this.showTutorial = true;
        }
    }

    checkSharedTable() {
        if (has(this.svcResponse, 'shared-table')) {
            this.showSharedTable = true;
        }
    }

    private checkRelatedActivities() {
        this.showRelatedActivities =
            has(this.svcResponse, 'relatedActivities.data') &&
            this.svcResponse.relatedActivities.data.length > 0;
    }

    private checkPromos() {
        this.showPromos =
            has(this.svcResponse, 'promos') &&
            this.svcResponse.promos.length > 0;
    }

    private setMediaEngineData(bundle: any) {
        if (!isNil(bundle.error) || !bundle.mediaEngine) {
            return;
        }
        this.mediaEngineData = bundle.mediaEngine;
    }

    private checkMoreLikeThis() {
        this.showMoreLikeThis =
            has(this.svcResponse, 'moreLikeThis') &&
            this.svcResponse.moreLikeThis.links.length > 0;
    }

    private checkStoryCards() {
        this.showStoryCards =
            has(this.svcResponse, 'storycards.data[0].sections') &&
            this.svcResponse.storycards.data[0].sections.length > 0;
    }

    private checkExtraEnchantment() {
        this.showExtraEnchantment =
            has(this.svcResponse, 'extraEnchantment') &&
            this.svcResponse.extraEnchantment.items.length > 0;
    }

    private checkKnowBeforeYouGo() {
        this.showKnowBeforeYouGo =
            has(this.svcResponse, 'knowBeforeYouGo') &&
            this.svcResponse.knowBeforeYouGo.items.length > 0;
    }

    private checkPackageCards() {
        this.showPackageCards = get(this.svcResponse, 'packageCards.packages', []).length > 0;
        this.showPackageCardsSeparator = !has(this.svcResponse, 'packageCards.packages[0].polyline');
    }

    private checkDisclaimer() {
        this.showDisclaimer =
            has(this.svcResponse, 'disclaimer') &&
            this.svcResponse.disclaimer.items.length > 0;
    }

    private checkParticipatingRestaurants() {
        this.showParticipatingRestaurants = has(this.svcResponse, 'participatingRestaurants') &&
        this.svcResponse.participatingRestaurants.restaurants.length > 0;
    }

    private checkSeatingChart() {
        this.showSeatingChart = has(this.svcResponse, 'seatingChart');
    }

    private checkMap() {
        // Check for override
        if (get(this.svcResponse, 'disableDetailMap') === true) {
            this.showMap = false;

            return;
        }

        this.showMap = has(this.svcResponse, 'map.location.markers');

        this.recenterControl = {
            title: get(this.svcResponse, 'map.recenterTitle'),
            icon: 'recenter-icon',
            callback: (mapObj: MapInterface, $event) => {
                $event.preventDefault();

                mapObj.focusOnParadeRoute();
            }
        };
    }

    private setupInterstitialData() {
        this.interstitialService.getInterstitialData()
            .pipe(takeUntil(this.destroy$))
            .subscribe(interstitialData => {
                this.interstitialModal = interstitialData;
            });
    }

    private setupTheming(data) {
        if (!data.theming) {
            return;
        }

        const el = this.elementRef.nativeElement;
        if (this.viewportDetection.isDesktop()) {
            el.style.setProperty('--detail-main-container-bg', `url(${data.theming?.desktop?.bgImage?.url})`);
            el.style.setProperty('--detail-main-container-bg-position', data.theming?.desktop?.bgPosition);
            el.style.setProperty('--detail-main-container-bg-repeat', data.theming?.desktop?.bgRepeat);
            el.style.setProperty('--detail-main-container-bg-size', data.theming?.desktop?.bgSize);
        } else {
            el.style.setProperty('--detail-main-container-bg', `url(${data.theming?.mobile?.bgImage?.url})`);
            el.style.setProperty('--detail-main-container-bg-position', data.theming?.mobile?.bgPosition);
            el.style.setProperty('--detail-main-container-bg-repeat', data.theming?.mobile?.bgRepeat);
            el.style.setProperty('--detail-main-container-bg-size', data.theming?.mobile?.bgSize);
        }
    }

    private startWebSocketConnection(snapshotData) {
        // Check to see if we should allow webSockets
        if (!snapshotData?.updatePermissions) {
            return;
        }

        this.webSocketService.detailConnection(
            snapshotData.id,
            snapshotData.updatePermissions
        );
    }
}
